// src/pages/help/PaymentsGuide.jsx
import React from 'react';
import { 
  ArrowLeft, CreditCard, CheckCircle, AlertCircle, 
  ExternalLink, Smartphone, Mail, Shield,
  DollarSign, Clock, Banknote, Settings, Copy, MessageSquare
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

const PaymentsGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8 max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate('/help')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Back to Help Center
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">Accept Credit Card Payments</h1>
      <p className="text-gray-600 mb-8">
        Get paid faster by accepting credit card payments directly through SuddyFlow
      </p>

      {/* Quick Overview */}
      <div className="card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white rounded-xl shadow-sm">
            <CreditCard className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">How It Works</h2>
            <p className="text-gray-700">
              Connect your bank account through Stripe (takes ~5 minutes), then send invoices 
              with payment links. Customers click, pay, and you get the money deposited 
              directly to your bank—usually within 2 business days.
            </p>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="card p-4 mb-8">
        <h3 className="font-semibold text-gray-900 mb-3">In This Guide</h3>
        <ul className="space-y-2 text-sm">
          <li>
            <a href="#setup" className="text-primary-600 hover:text-primary-700">
              1. Setting Up Your Payment Account
            </a>
          </li>
          <li>
            <a href="#payment-links" className="text-primary-600 hover:text-primary-700">
              2. Invoice Payment Links
            </a>
          </li>
          <li>
            <a href="#on-site" className="text-primary-600 hover:text-primary-700">
              3. Collecting Payment On-Site
            </a>
          </li>
          <li>
            <a href="#tracking" className="text-primary-600 hover:text-primary-700">
              4. Tracking Payments
            </a>
          </li>
          <li>
            <a href="#fees" className="text-primary-600 hover:text-primary-700">
              5. Fees & Payouts
            </a>
          </li>
          <li>
            <a href="#faq" className="text-primary-600 hover:text-primary-700">
              6. Frequently Asked Questions
            </a>
          </li>
        </ul>
      </div>

      {/* Section 1: Setup */}
      <section id="setup" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Settings className="text-primary-600" size={24} />
          1. Setting Up Your Payment Account
        </h2>
        
        <p className="text-gray-700 mb-4">
          Before you can accept payments, you need to connect your bank account through Stripe—the 
          same secure payment processor used by Amazon, Shopify, and millions of businesses worldwide.
        </p>

        <div className="card p-6 mb-4">
          <h3 className="font-semibold text-gray-900 mb-4">Step-by-Step Setup</h3>
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <div>
                <p className="font-medium text-gray-900">Go to Settings → Payments</p>
                <p className="text-sm text-gray-600">Find the Payments tab in your account settings</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <div>
                <p className="font-medium text-gray-900">Click "Connect with Stripe"</p>
                <p className="text-sm text-gray-600">You'll be redirected to Stripe's secure onboarding</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <div>
                <p className="font-medium text-gray-900">Enter Your Business Information</p>
                <p className="text-sm text-gray-600">Business name, address, and type (individual or company)</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
              <div>
                <p className="font-medium text-gray-900">Connect Your Bank Account</p>
                <p className="text-sm text-gray-600">
                  Use Plaid to link instantly, or manually enter your routing and account numbers
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
              <div>
                <p className="font-medium text-gray-900">Verify Your Identity</p>
                <p className="text-sm text-gray-600">
                  Last 4 of SSN (or EIN for companies). Stripe may ask for a photo ID.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">✓</span>
              <div>
                <p className="font-medium text-gray-900">You're Ready!</p>
                <p className="text-sm text-gray-600">
                  You'll see "Card Payments: Enabled" in your SuddyFlow settings
                </p>
              </div>
            </li>
          </ol>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-blue-900">Your Information is Secure</p>
              <p className="text-sm text-blue-700">
                SuddyFlow never sees your bank account details. Everything is handled directly 
                by Stripe, which is PCI Level 1 certified—the highest level of security in the 
                payments industry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Payment Links */}
      <section id="payment-links" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Mail className="text-primary-600" size={24} />
          2. Invoice Payment Links
        </h2>
        
        <p className="text-gray-700 mb-4">
          The easiest way to get paid—send your customer an invoice email with a "Pay Now" button. 
          They click it, enter their card, and you're done.
        </p>

        <div className="card p-6 mb-4">
          <h3 className="font-semibold text-gray-900 mb-4">How Customers Pay</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mail className="text-primary-600" size={24} />
              </div>
              <p className="font-medium text-gray-900 mb-1">1. Receive Email</p>
              <p className="text-sm text-gray-600">Customer gets invoice with Pay Now button</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CreditCard className="text-primary-600" size={24} />
              </div>
              <p className="font-medium text-gray-900 mb-1">2. Enter Card</p>
              <p className="text-sm text-gray-600">Secure Stripe checkout page</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <p className="font-medium text-gray-900 mb-1">3. Done!</p>
              <p className="text-sm text-gray-600">Invoice marked paid automatically</p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-amber-900">Tip: Enable Invoice Emails</p>
              <p className="text-sm text-amber-700">
                Make sure "Send Invoice Created" is enabled in{' '}
                <Link to="/settings?tab=emails" className="underline">Settings → Email Notifications</Link>{' '}
                so customers automatically receive their invoice with the payment link.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: On-Site Payments */}
      <section id="on-site" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Smartphone className="text-primary-600" size={24} />
          3. Collecting Payment On-Site
        </h2>
        
        <p className="text-gray-700 mb-4">
          Sometimes you want to collect payment before you leave the job site. Here are your options:
        </p>

        <div className="space-y-4">
          {/* Option 1: Copy & Text Payment Link */}
          <div className="card p-6 border-2 border-green-200 bg-green-50/30">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <MessageSquare className="text-green-600" size={24} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900">Option 1: Text the Payment Link</h3>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">Fastest!</span>
                </div>
                <p className="text-gray-600 mb-3">
                  Copy the payment link from the invoice and text it directly to the customer. 
                  They can pay right from their phone in seconds.
                </p>
                <ol className="text-sm text-gray-700 space-y-2 mb-3">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-green-700">1.</span>
                    <span>Open the invoice in SuddyFlow</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-green-700">2.</span>
                    <span>Tap <strong>"Copy Payment Link"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-green-700">3.</span>
                    <span>Paste it into a text message to the customer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-green-700">4.</span>
                    <span>Customer opens the link on their phone and pays</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-green-700">5.</span>
                    <span>You both get confirmation—done before you leave!</span>
                  </li>
                </ol>
                <div className="bg-green-100 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <strong>Pro Tip:</strong> This is the fastest way to collect payment on-site. 
                    No app download required—the customer just opens the link in their browser and pays!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Option 2: Send Invoice Email on the Spot */}
          <div className="card p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="text-blue-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Option 2: Send Invoice Email on the Spot</h3>
                <p className="text-gray-600 mb-3">
                  Create the invoice in SuddyFlow and send it while you're still with the customer.
                </p>
                <ol className="text-sm text-gray-700 space-y-2 mb-3">
                  <li>1. Complete the job in SuddyFlow</li>
                  <li>2. Create and send the invoice</li>
                  <li>3. Customer receives email immediately on their phone</li>
                  <li>4. They pay right there while you watch</li>
                </ol>
                <p className="text-sm text-gray-500">
                  Great for customers who prefer email receipts and a paper trail.
                </p>
              </div>
            </div>
          </div>

          {/* Option 3: Stripe Dashboard */}
          <div className="card p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ExternalLink className="text-purple-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Option 3: Stripe Dashboard (Manual Card Entry)</h3>
                <p className="text-gray-600 mb-3">
                  Use your phone to access the Stripe Dashboard and manually enter the customer's card info.
                </p>
                <ol className="text-sm text-gray-700 space-y-2 mb-3">
                  <li>1. Open SuddyFlow → Settings → Payments</li>
                  <li>2. Click "Open Stripe Dashboard"</li>
                  <li>3. Tap "Create Payment" or "+"</li>
                  <li>4. Enter the amount and customer's card details</li>
                  <li>5. Payment is processed instantly</li>
                </ol>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-sm text-purple-700">
                    <strong>Note:</strong> You'll need to manually mark the invoice as paid in SuddyFlow 
                    when using this method.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Option 4: Other Payment Methods */}
          <div className="card p-6 bg-gray-50">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-gray-200 rounded-lg">
                <Banknote className="text-gray-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Option 4: Cash, Check, Venmo, Zelle</h3>
                <p className="text-gray-600 mb-3">
                  Of course, you can always accept cash, check, Venmo, Zelle, or any other payment method. 
                  Just mark the invoice as "Paid" manually in SuddyFlow.
                </p>
                <p className="text-sm text-gray-500">
                  Remember to record the payment method for your records!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Future Feature Callout */}
        <div className="mt-4 bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Clock className="text-primary-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-primary-900">Coming Soon: In-App Card Entry</p>
              <p className="text-sm text-primary-700">
                We're working on a feature to let you enter card details directly in SuddyFlow 
                from the job or invoice screen. Stay tuned!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Tracking Payments */}
      <section id="tracking" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="text-primary-600" size={24} />
          4. Tracking Payments
        </h2>
        
        <p className="text-gray-700 mb-4">
          When a customer pays through a SuddyFlow payment link:
        </p>

        <ul className="space-y-3 mb-4">
          <li className="flex items-start gap-3">
            <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700">The invoice is automatically marked as <strong>Paid</strong></span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700">Payment date and method are recorded</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700">You can see it in your Dashboard and Reports</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700">Customer receives a payment confirmation email (if enabled)</span>
          </li>
        </ul>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-600 text-sm">
            <strong>Note:</strong> For payments made through the Stripe Dashboard directly, you'll need to 
            manually mark the invoice as paid in SuddyFlow to keep your records in sync.
          </p>
        </div>
      </section>

      {/* Section 5: Fees & Payouts */}
      <section id="fees" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="text-primary-600" size={24} />
          5. Fees & Payouts
        </h2>

        <div className="card p-6 mb-4">
          <h3 className="font-semibold text-gray-900 mb-4">Processing Fees</h3>
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
              <span className="text-gray-700">Stripe Processing Fee</span>
              <span className="font-bold text-gray-900">2.9% + $0.30</span>
            </div>
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
              <span className="text-gray-700">SuddyFlow Platform Fee</span>
              <span className="font-bold text-gray-900">0.5%</span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="font-semibold text-gray-900">Total Fee</span>
              <span className="font-bold text-primary-600 text-lg">3.4% + $0.30</span>
            </div>
          </div>
          
          <h4 className="font-medium text-gray-900 mb-2">Example</h4>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-gray-700 mb-3">Customer pays a <strong>$300 invoice</strong>:</p>
            <div className="space-y-1 text-sm text-gray-600 mb-3">
              <div className="flex justify-between">
                <span>Stripe fee (2.9% + $0.30)</span>
                <span>$9.00</span>
              </div>
              <div className="flex justify-between">
                <span>SuddyFlow fee (0.5%)</span>
                <span>$1.50</span>
              </div>
              <div className="flex justify-between border-t border-green-200 pt-1 mt-1">
                <span>Total fees</span>
                <span>$10.50</span>
              </div>
            </div>
            <div className="flex justify-between items-center bg-green-100 rounded-lg p-3">
              <span className="font-semibold text-green-900">You receive</span>
              <span className="font-bold text-green-700 text-xl">$289.50</span>
            </div>
          </div>
          
          <p className="text-sm text-gray-500 mt-4">
            The 0.5% platform fee helps us maintain and improve SuddyFlow's payment features. 
            It's only charged when you successfully collect a payment—no monthly fees, no hidden costs.
          </p>
        </div>

        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">When Do You Get Paid?</h3>
          <div className="flex items-start gap-3">
            <Clock className="text-primary-600 flex-shrink-0 mt-1" size={20} />
            <div>
              <p className="text-gray-700 mb-2">
                <strong>Standard payout schedule: 2 business days</strong>
              </p>
              <p className="text-sm text-gray-600">
                When a customer pays on Monday, the funds hit your bank Wednesday. 
                Stripe deposits automatically—no action needed from you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: FAQ */}
      <section id="faq" className="mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Frequently Asked Questions</h2>

        <div className="space-y-4">
          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">What if a customer disputes a charge?</h3>
            <p className="text-gray-600 text-sm">
              Stripe handles disputes (chargebacks) directly. You'll be notified via email and can 
              submit evidence through the Stripe Dashboard. Keep photos and signed agreements to 
              protect yourself!
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Can I refund a payment?</h3>
            <p className="text-gray-600 text-sm">
              Yes! Go to the Stripe Dashboard, find the payment, and click "Refund." You can do 
              full or partial refunds. Note: Processing fees are not returned on refunds.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">What cards are accepted?</h3>
            <p className="text-gray-600 text-sm">
              Visa, Mastercard, American Express, Discover, and most debit cards. Customers can 
              also use Apple Pay and Google Pay on supported devices.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Can I change my bank account later?</h3>
            <p className="text-gray-600 text-sm">
              Yes! Click "Open Stripe Dashboard" from Settings → Payments, then go to Settings → 
              Bank accounts to update your payout details.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Do I need a business bank account?</h3>
            <p className="text-gray-600 text-sm">
              No, you can use a personal checking account. However, we recommend a separate 
              business account to keep your finances organized (and your accountant happy!).
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Is there a monthly fee for payments?</h3>
            <p className="text-gray-600 text-sm">
              No monthly fees for accepting payments. You only pay the 3.4% + $0.30 per transaction 
              when you actually get paid. If you don't process any payments, you don't pay anything extra.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">What if my customer doesn't have email?</h3>
            <p className="text-gray-600 text-sm">
              No problem! Use the "Copy Payment Link" button and text it to them instead. 
              They can open the link in any browser on their phone and pay with their card.
            </p>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Can I disconnect my Stripe account?</h3>
            <p className="text-gray-600 text-sm">
              Yes, go to Settings → Payments and click "Disconnect." You can reconnect anytime. 
              Your payment history in Stripe is preserved even if you disconnect.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-2">Ready to Get Started?</h2>
        <p className="text-primary-100 mb-6">
          Connect your account in just 5 minutes and start getting paid faster.
        </p>
        <Link 
          to="/settings?tab=payments"
          className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
        >
          <CreditCard size={20} />
          Set Up Payments Now
        </Link>
      </div>
    </div>
  );
};

export default PaymentsGuide;